/** GeoJson utility functions for converting between miscellaneous formats without without making any change to
    the geometry itself. */

import { get } from 'lodash';

import { POLYGON_ARRAY_DEPTH, MULTIPOLYGON_ARRAY_DEPTH } from 'geometry/geojson/constants';

const _isPluralGeoJsonType = (type) => type === 'FeatureCollection' || type === 'GeometryCollection';

/** Gets the geometry part of a geoJson object. This is a useful function for normalizing geoJson values that might
    be a feature or geometry object.
    @param geoJson A valid geoJson object of singular type.
    @return Geometry for the object. */
export const getGeometry = (geoJson) => {
  if (!geoJson || !geoJson.type || _isPluralGeoJsonType(geoJson.type)) { throw new Error('Bad geoJson param.'); }
  if (geoJson.type === 'Feature') { return getGeometry(geoJson.geometry); } // Recursive call validates structure of geometry.
  if (!geoJson.coordinates) { throw new Error('Missing geoJson.coordinates'); } // RFC 7946 - 3.1 A GeoJSON Geometry object of any type other than "GeometryCollection" has a member with the name "coordinates"
  return geoJson;
};

/** Wraps geometry in a Feature as needed.
    @param geoJson A valid geoJson object of singular type.
    @return Unchanged geoJson param if already a Feature or the geoJson object wrapped in a new Feature object. */
export const getFeature = (geoJson) => {
  if (!geoJson || !geoJson.type || _isPluralGeoJsonType(geoJson.type)) { throw new Error('Bad geoJson param.'); }
  return (geoJson.type === 'Feature') ? geoJson : { type:'Feature', geometry:geoJson, properties:{} };
};

/** Does a GeoJson object have some kind of geometry specified?
    @param geoJson A geoJson object of any type.
    @return true if it does, false if not.
    @throws Never. (infallible) */
export const hasGeometry = (geoJson) => {
  try {
    return getGeometry(geoJson) !== null;
  } catch (ignored) {
    return false;
  }
};

/** Gets the coordinates part of a geoJson object.
    @param geoJson A valid geoJson object of singular type.
    @return Coordinates for the object. */
export const getCoordinates = (geoJson) => getGeometry(geoJson).coordinates;

// Note: not part of contract to check beyond first element for array depth.
export const getCoordinatesDepth = (array) => {
  if (!Array.isArray(array)) { return 0; }
  const firstElementDepth = (array.length > 0) ? getCoordinatesDepth(array[0]) : 0;
  return firstElementDepth + 1;
};

/** Converts coordinates representing one or more polygons to a Polygon or MultiPolygon.
    @param coordinates Valid coordinates for either polygon or multipolygon.
    @return Polygon if the coordinates don't define multiple polygons, or MultiPolygon if they do.
    @throws If unexpected array depth in coordinates. */
export const coordinatesToXPolygon = (coordinates) => {
  const arrayDepth = getCoordinatesDepth(coordinates);
  if (arrayDepth < POLYGON_ARRAY_DEPTH || arrayDepth > MULTIPOLYGON_ARRAY_DEPTH) { throw new Error('bad coordinates'); }

  let isPolygon = false;
  if (arrayDepth === POLYGON_ARRAY_DEPTH) {
    isPolygon = true;
  } else if (coordinates.length === 1) { // MultiPolygon coords that can be represented more simply as a Polygon.
    coordinates = coordinates[0];
    isPolygon = true;
  }

  const type = isPolygon ? 'Polygon' : 'MultiPolygon';
  return { type, coordinates };
};

/** use getCoordinates() instead if param might be a Feature containing an xPolygon. */
export const xPolygonToCoordinates = (xPolygon) => xPolygon.coordinates;

export const geometryCollectionToArray = (geometryCollection) => geometryCollection.geometries;

export const multiPolygonToFeatureArray = (geoJson) => {
  const coordinates = get(geoJson, 'geometry.coordinates');
  return !coordinates ? [] : coordinates.map(coords => {
    // Build new separate features that are polygons
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: coords
      },
      properties: {}
    };
  });
};

export const multiPolygonToPolygonArray = (multiPolygon) => {
  const multiPolygonGeometry = getGeometry(multiPolygon);
  if (!multiPolygonGeometry || multiPolygonGeometry.type !== 'MultiPolygon') {
    throw new Error('Bad multiPolygon param.');
  }
  const polygons = [];
  multiPolygonGeometry.coordinates.forEach(coordinates => {
    polygons.push({ type:'Polygon', coordinates });
  });
  return polygons;
};

export const xPolygonToPolygonArray = (geoJson) => {
  let geometry = getGeometry(geoJson);
  if (geometry.type === 'Polygon') {
    geometry = { type:'MultiPolygon', coordinates: [geometry.coordinates] };
  } else if (!geometry || geometry.type !== 'MultiPolygon') {
    throw new Error('Bad param.');
  }
  return multiPolygonToPolygonArray(geometry);
};

const _expandMultiPolygonsInPolygonArray = (xPolygonArray) => { // Side effect - filters out null polygons.
  // Look for MultiPolygons and collect their expansions.
  const polygonArray = xPolygonArray.filter(polygon => polygon && polygon.type === 'Polygon');
  let expandedPolygons = [];
  xPolygonArray.forEach(polygon => {
    if (polygon && polygon.type === 'MultiPolygon') {
      const innerPolygons = polygon.coordinates.map(coordinates => ({ type:'Polygon', coordinates }) );
      expandedPolygons = expandedPolygons.concat(innerPolygons);
    }
  });
  if (!expandedPolygons.length) { return polygonArray; } // No expansions.

  // Return non-expanded Polygons along with the expanded Polygons.
  return expandedPolygons.concat(polygonArray);
};

/** @param polygonArray Each element is either Polygon or MultiPolygon.
    @return Polygon or MultiPolygon that represents all passed polygons. */
export const polygonArrayToXPolygon = (polygonArray) => {
  polygonArray = _expandMultiPolygonsInPolygonArray(polygonArray);
  if (polygonArray.length === 1) {
    return polygonArray[0];
  } else if (polygonArray.length > 1) { // Combine multiple polygons into a MultiPolygon.
    return {
      type:'MultiPolygon',
      coordinates:polygonArray.map(polygon => polygon.coordinates)
    };
  } else {
    throw new Error('Bad polygonArray param');
  }
};
